import React, { createContext, useContext, useState, useEffect } from 'react';

export interface Review {
  id: string;
  productId: string;
  productType: 'device' | 'report';
  userId: string;
  userName: string;
  rating: number;
  title: string;
  comment: string;
  date: Date;
  verified: boolean;
  helpful: number;
  images?: string[];
}

interface ReviewContextType {
  reviews: Review[];
  addReview: (review: Omit<Review, 'id' | 'date' | 'helpful'>) => void;
  getProductReviews: (productId: string, productType: 'device' | 'report') => Review[];
  getAverageRating: (productId: string, productType: 'device' | 'report') => number;
  markHelpful: (reviewId: string) => void;
}

const ReviewContext = createContext<ReviewContextType | undefined>(undefined);

export const useReviews = (): ReviewContextType => {
  const context = useContext(ReviewContext);
  if (!context) {
    throw new Error('useReviews must be used within a ReviewProvider');
  }
  return context;
};

// Mock reviews data
const mockReviews: Review[] = [
  {
    id: '1',
    productId: 'catapult',
    productType: 'device',
    userId: '1',
    userName: 'John D.',
    rating: 5,
    title: 'Game changing device!',
    comment: 'This device has completely transformed how I analyze my soccer performance. The data is incredibly detailed and accurate.',
    date: new Date('2024-01-15'),
    verified: true,
    helpful: 12,
    images: []
  },
  {
    id: '2',
    productId: 'catapult',
    productType: 'device',
    userId: '2',
    userName: 'Sarah M.',
    rating: 4,
    title: 'Great product, minor setup issues',
    comment: 'The analytics are fantastic once you get it set up. Had some initial connectivity issues but customer support was helpful.',
    date: new Date('2024-01-10'),
    verified: true,
    helpful: 8,
    images: []
  },
  {
    id: '3',
    productId: 'catapult',
    productType: 'device',
    userId: '3',
    userName: 'Mike R.',
    rating: 5,
    title: 'Professional grade analytics',
    comment: 'Using this for our college team. The insights into player load and performance are incredible. Worth every penny.',
    date: new Date('2024-01-08'),
    verified: true,
    helpful: 15,
    images: []
  },
  {
    id: '4',
    productId: 'performance-analysis',
    productType: 'report',
    userId: '4',
    userName: 'Coach Williams',
    rating: 5,
    title: 'Detailed insights for team improvement',
    comment: 'These reports helped us identify key areas for improvement. Our team performance has increased significantly.',
    date: new Date('2024-01-05'),
    verified: true,
    helpful: 20,
    images: []
  }
];

export const ReviewProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [reviews, setReviews] = useState<Review[]>(mockReviews);

  useEffect(() => {
    // Load reviews from localStorage
    const storedReviews = localStorage.getItem('product_reviews');
    if (storedReviews) {
      const parsedReviews = JSON.parse(storedReviews).map((review: any) => ({
        ...review,
        date: new Date(review.date),
      }));
      setReviews([...mockReviews, ...parsedReviews]);
    }
  }, []);

  const saveReviews = (updatedReviews: Review[]) => {
    setReviews(updatedReviews);
    const userReviews = updatedReviews.filter(r => !mockReviews.find(mr => mr.id === r.id));
    localStorage.setItem('product_reviews', JSON.stringify(userReviews));
  };

  const addReview = (reviewData: Omit<Review, 'id' | 'date' | 'helpful'>) => {
    const newReview: Review = {
      ...reviewData,
      id: `review-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      date: new Date(),
      helpful: 0,
    };

    const updatedReviews = [...reviews, newReview];
    saveReviews(updatedReviews);
  };

  const getProductReviews = (productId: string, productType: 'device' | 'report'): Review[] => {
    return reviews
      .filter(review => review.productId === productId && review.productType === productType)
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  };

  const getAverageRating = (productId: string, productType: 'device' | 'report'): number => {
    const productReviews = getProductReviews(productId, productType);
    if (productReviews.length === 0) return 0;
    
    const totalRating = productReviews.reduce((sum, review) => sum + review.rating, 0);
    return Math.round((totalRating / productReviews.length) * 10) / 10;
  };

  const markHelpful = (reviewId: string) => {
    const updatedReviews = reviews.map(review =>
      review.id === reviewId
        ? { ...review, helpful: review.helpful + 1 }
        : review
    );
    saveReviews(updatedReviews);
  };

  return (
    <ReviewContext.Provider
      value={{
        reviews,
        addReview,
        getProductReviews,
        getAverageRating,
        markHelpful,
      }}
    >
      {children}
    </ReviewContext.Provider>
  );
};